// User roles - matching Flutter app
export enum UserRole {
  RECOMMENDER = 'recommender',
  AGENCY = 'agency',
  ADMIN = 'admin'
}

// User status - matching Flutter app
export enum UserStatus {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  PENDING = 'pending'
}

// Referral status - matching Flutter app ReferralStatus
export enum ReferralStatus {
  PENDING = 'pending',
  CONTACTED = 'contacted',
  MEETING = 'meeting',
  SOLD = 'sold',
  LOST = 'lost',
  EXPIRED = 'expired'
}

// Commission status
export enum CommissionStatus {
  PENDING = 'pending',
  VALIDATED = 'validated',
  PAID = 'paid'
}

// User model - matching Flutter app UserModel
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
  createdAt: Date;
  isActive: boolean;
  status: UserStatus;
  profileData?: Map<string, any>;
  trackingToken?: string;
  agencyId?: string;
  agencyAddress?: string;
  agencyCity?: string;
  agencyPostalCode?: string;
  agencySiren?: string;
  agencyLatitude?: number;
  agencyLongitude?: number;
  agencyCoverageAreas?: string[];
  commissionRatePerLead?: number;
}

// Referral model - matching Flutter app ReferralModel
export interface Referral {
  id: string;
  recommenderId: string;
  agencyId?: string;
  sellerFirstName: string;
  sellerLastName: string;
  sellerPhone: string;
  sellerEmail: string;
  propertyAddress: string;
  propertyType: string;
  propertyDescription?: string;
  status: ReferralStatus;
  createdAt: Date;
  updatedAt?: Date;
  gdprConsent: boolean;
  trackingToken: string;
  notes?: string;
  commissionAmount?: number;
  soldDate?: Date;
  soldPrice: number;
  images: string[];
}

// Property model - matching Flutter app PropertyModel
export interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: number;
  type: string;
  status: string;
  images: string[];
  details: PropertyDetails;
  createdAt: Date;
  updatedAt: Date;
  ownerId: string;
  agencyId?: string;
}

export interface PropertyDetails {
  bedrooms?: number;
  bathrooms?: number;
  squareFootage?: number;
  yearBuilt?: number;
  amenities: string[];
  description?: string;
}

// Commission model - derived from Referrals
export interface Commission {
  id: string;
  referralId: string;
  recommenderId: string;
  recommenderName: string;
  agencyId: string;
  agencyName: string;
  amount: number;
  status: CommissionStatus;
  createdAt: Date;
  paidAt?: Date;
  paymentMethod?: string;
}

// KPI data - calculated from real data
export interface KPIData {
  leadsGenerated: {
    day: number;
    week: number;
    month: number;
  };
  signedMandates: {
    day: number;
    week: number;
    month: number;
  };
  conversionRate: number;
  commissionsToPay: number;
  commissionsValidated: number;
}

// Chart data - calculated from real data
export interface ChartData {
  leadsPerMonth: Array<{ month: string; count: number }>;
  conversionFunnel: Array<{ stage: string; count: number; color: string }>;
  topSponsors: Array<{ name: string; leads: number; conversion: number }>;
  topAgencies: Array<{ name: string; leads: number; mandates: number }>;
}

// Geographic data - from referral locations
export interface GeographicData {
  region: string;
  leads: number;
  conversions: number;
  revenue: number;
  agencies: number;
  conversionRate: number;
}

// Support ticket
export interface SupportTicket {
  id: string;
  userId: string;
  userName: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string;
  resolution?: string;
}

// Admin configuration
export interface AdminConfig {
  maxAgencyDistanceKm: number;
  fallbackMaxDistanceKm: number;
  minCommissionRate: number;
  commissionRules: {
    fixed: number;
    percentage: number;
    bonus: number;
    bonusThreshold: number;
  };
  referralCodes: {
    prefix: string;
    length: number;
    active: boolean;
  };
}



